import React, { ReactElement, useState } from 'react';
import { useRouter } from 'next/router';
import {
    getTranslation,
    getTranslationKey,
    useTranslation,
} from 'utils/localization';
import AppLayout from 'components/layouts/AppLayout';
import { TNamespaceEnum } from 'types/types';
import {
    FilterSize,
    GroupEditFieldInterface,
    LabelSize,
    StateInterface,
    ValidateValueType,
    ValueOnChangeRemoveType,
} from 'components/interfaces/GeneralInterface';
import { FieldTypeApiEnum } from 'submodules/api_middleware';
import EditForm from 'components/editForm/EditForm';
import { OnActionType } from 'components/interfaces/EditFormInterface';
import {
    parseCzechBirthNumber,
    slashCzechBirthNumber,
    unslashCzechBirthNumber,
} from 'utils/index';
import { fetcherFull, MethodEnum } from 'utils/helpers/browser/newFetcher';
import { MessageType, pushMessage } from 'components/layouts/Toast';
import PaymentDetail from 'components/anonymous-payment/PaymentDetail';

import { getRoute } from 'constants/routes';
import styles from './AnonymousPaymentPage.module.scss';

/**
 * Page for personal (anonymous) payment. Has two fields (internal_id and birth_number). If combination is correct,
 * displays the payment instructions.
 */
const AnonymousPaymentPage = (): ReactElement => {
    const router = useRouter();
    const { t } = useTranslation();
    const [displayNationalIdentifier, setDisplayNationalIdentifier] =
        useState(false);
    const [success, setSuccess] = useState(false);
    const [memberId, setMemberId] = useState(undefined);
    const [nationalId, setNationalId] = useState(undefined);

    const onFormSubmit: OnActionType = async (state) => {
        if (displayNationalIdentifier) {
            return onNationalIdentifierFormSubmit(state);
        } else {
            setMemberId(state.member_id);
            return onCheckAnonymousPaymentMemberFormSubmit(state);
        }
    };

    const onCheckAnonymousPaymentMemberFormSubmit: OnActionType = async (
        state
    ) => {
        const response = await fetcherFull<any>(
            '/api/anonymous-payment/check-member',
            MethodEnum.POST,
            undefined,
            state
        );

        if (response.error) {
            pushMessage(
                getTranslation(
                    t,
                    TNamespaceEnum.anonymousPayment,
                    'invalid_anonymous_payment_text'
                ),
                MessageType.DANGER,
                getTranslation(
                    t,
                    TNamespaceEnum.anonymousPayment,
                    'invalid_member_id_title'
                ),
                true
            );
            return response;
        }

        const isArchiveMember = response.data?.data?.is_archive_member;
        if (isArchiveMember) {
            await router.push(
                getRoute('/person-recovery', [], {
                    messageType: MessageType.DANGER,
                    message: getTranslation(
                        t,
                        TNamespaceEnum.anonymousPayment,
                        'member_is_archive_text'
                    ),
                    messageTitle: getTranslation(
                        t,
                        TNamespaceEnum.anonymousPayment,
                        'member_is_archive_title'
                    ),
                })
            );
        }

        const isPaidMembership = response.data?.data?.is_paid_membership;
        if (isPaidMembership) {
            pushMessage(
                getTranslation(
                    t,
                    TNamespaceEnum.anonymousPayment,
                    'invalid_anonymous_payment_text'
                ),
                MessageType.DANGER,
                getTranslation(
                    t,
                    TNamespaceEnum.anonymousPayment,
                    'membership_is_paid_title'
                ),
                true
            );
        } else {
            const isNationalIdRequired =
                response.data?.data?.is_national_id_required;
            if (isNationalIdRequired) {
                setDisplayNationalIdentifier(isNationalIdRequired);
            } else {
                // move to the final step without setting the national identifier
                // always set the national identifier before showing the payment data component
                // otherwise, the BE API can be called with a wrong value of the national identifier
                setNationalId(null);
                setSuccess(true);
            }
        }

        return response;
    };

    const onNationalIdentifierFormSubmit: OnActionType = async (state) => {
        const response = await fetcherFull<any>(
            '/api/anonymous-payment',
            MethodEnum.POST,
            undefined,
            state
        );

        if (response.error) {
            pushMessage(
                getTranslation(
                    t,
                    TNamespaceEnum.anonymousPayment,
                    'invalid_anonymous_payment_text'
                ),
                MessageType.DANGER,
                getTranslation(
                    t,
                    TNamespaceEnum.anonymousPayment,
                    'membership_is_paid_title'
                ),
                true
            );
            return response;
        }

        const correct = response.data?.data?.correct;
        setNationalId(unslashCzechBirthNumber(state.national_identifier));
        setSuccess(correct);

        if (!correct) {
            pushMessage(
                getTranslation(
                    t,
                    TNamespaceEnum.anonymousPayment,
                    'invalid_anonymous_payment_text'
                ),
                MessageType.DANGER,
                getTranslation(
                    t,
                    TNamespaceEnum.anonymousPayment,
                    'invalid_combination_title'
                ),
                true
            );
        }

        return response;
    };

    // Form groups and fields
    const fields: GroupEditFieldInterface[] = [
        {
            title: getTranslation(
                t,
                TNamespaceEnum.anonymousPayment,
                'anonymousPaymentTitle'
            ),
            fields: [
                {
                    name: 'member_id',
                    label: getTranslationKey(
                        TNamespaceEnum.anonymousPayment,
                        'form.member_id'
                    ),
                    type: FieldTypeApiEnum.TEXT,
                    size: FilterSize.FULL,
                    labelSize: LabelSize.L,
                    required: true,
                    disabled: displayNationalIdentifier || success, // the next or the final step
                },
                {
                    name: 'national_identifier',
                    label: getTranslationKey(
                        TNamespaceEnum.anonymousPayment,
                        'form.national_identifier'
                    ),
                    type: FieldTypeApiEnum.TEXT,
                    size: FilterSize.FULL,
                    labelSize: LabelSize.L,
                    validate: (value: ValidateValueType) => {
                        const validationRes = parseCzechBirthNumber(
                            String(value)
                        );

                        // Invalid id
                        if (!validationRes.valid)
                            return t(validationRes.errorMessageKey);

                        return undefined;
                    },
                    displayIfSuccess: () => displayNationalIdentifier,
                    onChange: (
                        value: ValueOnChangeRemoveType,
                        state: StateInterface
                    ) => {
                        const strVal = String(value);
                        state.national_identifier = slashCzechBirthNumber(
                            true,
                            strVal
                        );
                        return state;
                    },
                    required: true,
                },
            ],
        },
    ];

    return (
        <AppLayout
            pageTitle={{
                primaryBoldText: getTranslation(
                    t,
                    TNamespaceEnum.anonymousPayment,
                    'pageTitle'
                ),
                isDark: true,
            }}
            hideUserMenu={false}
            showDisciplineMenu
            changeDisciplineWithoutReload={true}
            showLangMenu={true}
        >
            <div>
                {!success && (
                    <div className={styles.formWrapper}>
                        <EditForm
                            fields={fields}
                            buttonTextKey={getTranslationKey(
                                TNamespaceEnum.anonymousPayment,
                                'form.submit'
                            )}
                            action={onFormSubmit}
                        />
                    </div>
                )}
                {success && (
                    <PaymentDetail
                        memberId={memberId}
                        nationalIdentifier={nationalId}
                        nationalIdentifierIsRequired={displayNationalIdentifier}
                    />
                )}
            </div>
        </AppLayout>
    );
};

export default AnonymousPaymentPage;
